import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { Analytics } from '@vercel/analytics/react';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Work from './pages/Work';
import ProjectDetail from './pages/ProjectDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProjects from './pages/admin/AdminProjects';
import AdminCategories from './pages/admin/AdminCategories';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminMessages from './pages/admin/AdminMessages';
import AdminAbout from './pages/admin/AdminAbout';
import AdminContact from './pages/admin/AdminContact';
import { analyticsAPI } from './utils/api';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return admin ? children : <Navigate to="/admin/login" replace />;
};

const PublicLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
};

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;

    if (pathname.startsWith('/admin') || pathname.startsWith('/projects/')) {
      return;
    }

    const routeMap = {
      '/': { page_key: 'home', page_title: 'Home' },
      '/work': { page_key: 'work', page_title: 'Work' },
      '/about': { page_key: 'about', page_title: 'About' },
      '/contact': { page_key: 'contact', page_title: 'Contact' }
    };

    const routeMeta = routeMap[pathname] || {
      page_key: pathname.replace(/\//g, '_').replace(/^_+/, '') || 'unknown',
      page_title: pathname
    };

    analyticsAPI.trackPageView({
      project_id: '',
      project_title: '',
      page_key: routeMeta.page_key,
      page_title: routeMeta.page_title,
      event_type: 'page_view',
      tab_name: '',
      user_agent: navigator.userAgent,
      ip_address: ''
    }).catch((error) => {
      console.error('Page analytics tracking failed:', error);
    });
  }, [location.pathname]);

  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <div className="App smooth-scroll">
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/work" element={<PublicLayout><Work /></PublicLayout>} />
            <Route path="/projects/:id" element={<PublicLayout><ProjectDetail /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="about" element={<AdminAbout />} />
              <Route path="contact" element={<AdminContact />} />
            </Route>
          </Routes>
          <Toaster position="top-right" />
        </div>
        <Analytics />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
